import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  fetchLessonById,
  clearSelectedEntity,
  selectSelectedLesson,
  selectLessonDetailStatus,
  selectLessonDetailError,
} from '../../features/lessons/lessonSlice';
import { selectUserInformations } from '../../features/authSlice';
import { InputField } from '../../components/reusable/InputField';
import ToggleSwitch from '../../components/reusable/ToggleSwitch';
import lessonFields from '../../config/lessonFields';
import { showToast } from '../../utils/utils';
import Loader from '../../components/Live/Loader';
import { FiEdit, FiArrowLeft } from 'react-icons/fi';

/**
 * Preview Lesson Page
 * Full page read-only view of lesson details
 */
const PreviewLessonPage = () => {
  const { id, courseId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);
  const location = useLocation();
  const course = location.state?.course;

  const selectedLesson = useSelector(selectSelectedLesson);
  const detailStatus = useSelector(selectLessonDetailStatus);
  const detailError = useSelector(selectLessonDetailError);

  useEffect(() => {
    if (id && user?.token) {
      dispatch(fetchLessonById({ token: user.token, lessonId: id }));
    }

    return () => {
      dispatch(clearSelectedEntity());
    };
  }, [id, user?.token, dispatch]);

  useEffect(() => {
    if (detailStatus === 'failed' && detailError) {
      showToast('Error', detailError, 'error');
      navigate(`/dashboard/courses/lesson/${courseId || selectedLesson?.course_id}`, { replace: true, state: { course } });
    }
  }, [detailStatus, detailError, navigate, courseId, selectedLesson, course]);

  const loading = detailStatus === 'loading';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (detailStatus === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{detailError}</p>
          <button
            onClick={() => navigate(`/dashboard/courses/lesson/${courseId}`, { state: { course } })}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Lessons
          </button>
        </div>
      </div>
    );
  }

  if (!selectedLesson) {
    return null;
  }

  const textFields = lessonFields.filter(field => 
    (field.type === 'text' || field.type === 'number') && 
    field.name !== 'course_id'
  );
  const textareaField = lessonFields.find(field => field.type === 'textarea');
  const toggleField = lessonFields.find(field => field.type === 'toggle');

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.1,
        when: "beforeChildren"
      }
    },
    exit: { opacity: 0, y: -20 }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 }
  };

  return (
    <div className="min-h-screen py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-4">
              <Link
                to={`/dashboard/courses/lesson/${courseId || selectedLesson?.course_id}`}
                state={{ course }}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800"
              >
                <FiArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white capitalize">
                  Lesson Details
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  View lesson information
                </p>
              </div>
            </div>
            <Link
              to={`/dashboard/courses/${courseId || selectedLesson?.course_id}/lessons/${id}/edit`}
              state={{ course }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiEdit className="w-4 h-4" />
              <span>Edit Lesson</span>
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={containerVariants}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 lg:p-8 relative" style={{ overflow: 'visible' }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {textFields.map((field, index) => (
              <motion.div
                key={field.name}
                variants={itemVariants}
                transition={{ delay: index * 0.05 }}
              >
                <InputField
                  label={field.label}
                  name={field.name}
                  placeholder={field.placeholder}
                  value={field.name === 'course_id' && selectedLesson.course ? selectedLesson.course.title : (selectedLesson[field.name] || '')}
                  disabled={true}
                />
              </motion.div>
            ))}

            {toggleField && (
              <motion.div
                key={toggleField.name}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <ToggleSwitch
                  label={toggleField.label}
                  checked={selectedLesson[toggleField.name] === 'active' || !!selectedLesson[toggleField.name]}
                  className="space-x-2"
                  disabled={true}
                />
              </motion.div>
            )}

            {textareaField && (
              <motion.div
                key={textareaField.name}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="sm:col-span-2"
              >
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {textareaField.label}
                </label>
                <textarea
                  name={textareaField.name}
                  className="w-full h-32 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed resize-none overflow-y-auto dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder={textareaField.placeholder}
                  value={selectedLesson[textareaField.name] || ''}
                  disabled={true}
                />
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PreviewLessonPage;
